<template>
  <div ref="rootRef" class="relative">
    <button
      ref="menuTrigger"
      type="button"
      class="relative z-10 flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1.5 text-sm font-medium transition-colors lg:px-3.5"
      :class="
        isOpen || item.active
          ? 'bg-white text-brand-600 dark:bg-zinc-800 dark:text-brand-400'
          : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
      "
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-haspopup="true"
      :aria-label="item.name"
      @click="toggle"
    >
      <NavIcon
        :name="item.icon"
        class="size-4 shrink-0"
        :class="
          isOpen || item.active
            ? 'text-brand-600 dark:text-brand-400'
            : 'text-current'
        "
        width="16"
        height="16"
      />
      <span class="hidden lg:inline">{{ item.name }}</span>
      <Icon
        type="chevron-down"
        class="size-3.5 shrink-0 transition-transform duration-200"
        :class="isOpen ? 'rotate-180' : ''"
      />
    </button>

    <teleport to="body">
      <div
        v-if="isOpen"
        class="panel-mega-menu-backdrop fixed inset-0 z-[99]"
        aria-hidden="true"
        @click="close"
      />

      <transition
        enter-active-class="panel-mega-menu-dropdown-enter-active"
        enter-from-class="panel-mega-menu-dropdown-enter-from"
        enter-to-class="panel-mega-menu-dropdown-enter-to"
        leave-active-class="panel-mega-menu-dropdown-leave-active"
        leave-from-class="panel-mega-menu-dropdown-leave-from"
        leave-to-class="panel-mega-menu-dropdown-leave-to"
      >
        <div
          v-if="isOpen"
          ref="menuPanel"
          class="panel-mega-menu-dropdown-panel pointer-events-auto fixed z-[100]"
          :style="panelStyle"
        >
          <div class="panel-glass panel-mega-menu overflow-hidden">
            <div class="panel-glass-content panel-mega-menu__header">
              <p class="panel-mega-menu__title">
                {{ item.name }}
              </p>
            </div>

            <div
              class="panel-glass-content panel-mega-menu__grid panel-mega-menu__grid--column-flow"
              :style="gridStyle"
            >
              <Link
                v-for="menuItem in item.items"
                :key="menuItem.key"
                :href="menuItem.path"
                class="panel-mega-menu__standalone"
                :class="{ 'panel-mega-menu__standalone--active': menuItem.active }"
                :aria-current="menuItem.active ? 'page' : undefined"
                @click="close"
              >
                <span
                  v-if="menuItem.icon"
                  class="panel-mega-menu__standalone-icon"
                  aria-hidden="true"
                >
                  <NavIcon :name="menuItem.icon" width="15" height="15" />
                </span>
                <span class="min-w-0 truncate">{{ menuItem.name }}</span>
              </Link>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/vue'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { onClickOutside } from '@vueuse/core'
import NavIcon from './NavIcon.vue'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const rootRef = ref(null)
const menuTrigger = ref(null)
const menuPanel = ref(null)

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

const { floatingStyles } = useFloating(menuTrigger, menuPanel, {
  strategy: 'fixed',
  placement: 'bottom',
  transform: false,
  whileElementsMounted: autoUpdate,
  middleware: [
    offset(12),
    flip(),
    shift({ padding: 16 }),
    size({
      /**
       * Constrain panel width while keeping it within the viewport
       *
       * @param {{ availableWidth: number, elements: { floating: HTMLElement } }} state
       */
      apply({ availableWidth, elements }) {
        Object.assign(elements.floating.style, {
          width: `${Math.min(448, availableWidth - 32)}px`,
        })
      },
    }),
  ],
})

/**
 * Merged floating-ui styles for the teleported panel
 */
const panelStyle = computed(() => ({
  zIndex: 100,
  ...floatingStyles.value,
}))

/**
 * Grid row template for column-first mega menu layout
 */
const gridStyle = computed(() => {
  const itemCount = props.item.items?.length ?? 0
  const rowCount = Math.max(1, Math.ceil(itemCount / 2))

  return {
    gridTemplateRows: `repeat(${rowCount}, auto)`,
  }
})

let stopClickOutside

/**
 * Close the nav group dropdown panel
 */
function close() {
  isOpen.value = false
}

/**
 * Toggle the nav group dropdown panel
 *
 * @param {Event} event
 */
function toggle(event) {
  event.stopPropagation()
  isOpen.value = !isOpen.value
}

/**
 * Bind click-outside handling for the dropdown panel
 */
function bindClickOutside() {
  stopClickOutside?.()
  stopClickOutside = onClickOutside(
    rootRef,
    () => close(),
    { ignore: [menuPanel] }
  )
}

/**
 * Close the dropdown when Escape is pressed
 *
 * @param {KeyboardEvent} event
 */
function handleEscape(event) {
  if (event.key === 'Escape') {
    close()
  }
}

watch(
  () => props.modelValue,
  open => {
    if (open) {
      nextTick(() => bindClickOutside())
      document.addEventListener('keydown', handleEscape)
      return
    }

    stopClickOutside?.()
    stopClickOutside = undefined
    document.removeEventListener('keydown', handleEscape)
  }
)

onBeforeUnmount(() => {
  stopClickOutside?.()
  document.removeEventListener('keydown', handleEscape)
})
</script>
